// High-level Plaid workflows. Glue between plaid_client + plaid_link +
// plaid_sync + plaid_storage + the vault. main.ts owns the DOM modals;
// this module owns the data flow.
//
// Two workflows:
//   connectNewBank(creds, ui) — runs the full "add a bank" sequence:
//     1. POST /link/token/create
//     2. Open Plaid Link
//     3. Exchange the public_token for an access_token
//     4. Ask the UI to show the account selector
//     5. Persist the PlaidItem with the user's selected_account_ids
//     6. Return the new item so the caller can kick off the first sync
//
//   pullAllItems(creds, onResult) — for each stored item, pulls
//   /transactions/sync, writes the synthetic JSON via the supplied
//   callback (caller hands it to processFiles), updates last_cursor
//   + last_sync. Surfaces errors per-item so one broken Item doesn't
//   block the others.
//
// The UI side is parameterised — these workflows know nothing about
// modals, buttons, or DOM. main.ts plugs in the appropriate prompts
// via the AccountPicker / SyncWriter interfaces below.

import {
  linkTokenCreate,
  exchangePublicToken,
  PlaidApiError,
  type PlaidCredentials,
} from "./plaid_client.js";
import { openPlaidLink, type PlaidLinkSuccessMetadata } from "./plaid_link.js";
import {
  type PlaidItem,
  type KnownAccount,
  upsertItem,
  listItems,
  updateItemAfterSync,
  setItemNeedsRelink,
} from "./plaid_storage.js";
import { pullPlaidSync, type SyncPullResult } from "./plaid_sync.js";
import * as log from "../log.js";

/** Caller-supplied UI prompts. Both methods receive a snapshot of the
 *  accounts Plaid surfaced; the caller renders a checkbox list and
 *  resolves with the chosen account_ids. */
export interface AccountPicker {
  /** Show the account selector for a fresh Item just returned from
   *  Plaid Link onSuccess. Reject the returned promise (or throw)
   *  to abort the connect flow — the access_token will be discarded. */
  pickForNewItem(args: {
    institutionName: string;
    accounts: KnownAccount[];
  }): Promise<{ selected: string[] }>;
}

/** Caller-supplied side effects for the pull-all flow. */
export interface SyncWriter {
  /** Write the synthetic Plaid JSON into the user's data folder and
   *  feed it through processFiles. Called once per item per pull. */
  acceptResult(item: PlaidItem, result: SyncPullResult): Promise<void>;
  /** Called when a single item's sync fails. The pullAllItems loop
   *  continues with the next item. */
  reportItemError?(item: PlaidItem, err: unknown): void;
}

// ── connectNewBank ─────────────────────────────────────────────────

function metadataToKnownAccounts(meta: PlaidLinkSuccessMetadata): KnownAccount[] {
  return (meta.accounts ?? []).map((a) => ({
    account_id: a.id,
    name: a.name,
    mask: a.mask,
    subtype: a.subtype,
    type: a.type,
  }));
}

// ── OAuth-resume session storage ──────────────────────────────────
//
// Plaid Link uses two patterns for OAuth banks:
//   1. POPUP: Link opens the bank's OAuth in a popup window. After
//      auth, the popup closes itself and posts a message back to the
//      parent. The parent's Link instance handles it transparently —
//      no page reload, no special code on our side.
//   2. FULL-PAGE REDIRECT: on mobile / popup-blocked browsers, Link
//      navigates the parent tab to the bank's OAuth page. After auth,
//      the bank redirects to our app's registered redirect_uri with
//      ?oauth_state_id=<...>. The page reloads fresh, our Link
//      instance is gone, we need to reconstruct it with the original
//      link_token + the full redirect URL.
//
// Pattern 1 needs only redirect_uri passed to /link/token/create.
// Pattern 2 additionally needs us to save the link_token before
// opening Link, then on app boot detect the OAuth-return URL and
// resume Link with `receivedRedirectUri`.

const PENDING_LINK_KEY = "pf:plaid:pending-link";

interface PendingLinkSession {
  link_token: string;
  /** ISO timestamp of when we created the token. Used to guard against
   *  resuming with an expired token — Plaid invalidates link_tokens
   *  after ~30 minutes. */
  created_at: string;
  redirect_uri: string;
}

function getPendingSession(): PendingLinkSession | null {
  if (typeof sessionStorage === "undefined") return null;
  const raw = sessionStorage.getItem(PENDING_LINK_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as PendingLinkSession;
    if (Date.now() - new Date(s.created_at).getTime() > 30 * 60 * 1000) {
      sessionStorage.removeItem(PENDING_LINK_KEY);
      return null;
    }
    return s;
  } catch {
    sessionStorage.removeItem(PENDING_LINK_KEY);
    return null;
  }
}

function savePendingSession(session: PendingLinkSession): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(PENDING_LINK_KEY, JSON.stringify(session));
}

function clearPendingSession(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(PENDING_LINK_KEY);
}

/** Compute the redirect_uri to pass to /link/token/create. Pulls from
 *  window.location so it stays in sync with whatever URL the user is
 *  on (production, Pages preview, custom domain, localhost). Strips
 *  search params + hash so it matches what the user registers in their
 *  Plaid dashboard. Returns undefined when window is unavailable
 *  (test env, server-side render). */
export function computeRedirectUri(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return `${window.location.origin}${window.location.pathname}`;
}

/** Run the full "add a bank" workflow. Returns the persisted PlaidItem
 *  (post account-selection). Throws if the user cancels Plaid Link or
 *  the account picker.
 *
 *  Passes redirect_uri to Plaid so OAuth banks (Chase, BofA, Wells,
 *  Citi, Cap1, etc.) appear in the picker. The user must register
 *  the redirect URI in their Plaid dashboard once — until they do,
 *  Plaid responds with INVALID_REDIRECT_URI on /link/token/create.
 *  We auto-detect that error and retry WITHOUT redirect_uri so the
 *  non-OAuth banks still work; a warning lands in the log telling
 *  them to register the URL if they want OAuth banks. */
export async function connectNewBank(
  creds: PlaidCredentials,
  picker: AccountPicker,
): Promise<PlaidItem> {
  const redirectUri = computeRedirectUri();
  log.info("plaid", "connectNewBank: creating link_token", { redirectUri });
  let linkToken: string;
  try {
    const resp = await linkTokenCreate(creds, { redirectUri });
    linkToken = resp.link_token;
  } catch (err) {
    if (
      err instanceof PlaidApiError &&
      err.body.error_code === "INVALID_REDIRECT_URI"
    ) {
      log.warn(
        "plaid",
        `Plaid rejected redirect_uri (${redirectUri ?? "<none>"}). Register it in your Plaid dashboard under "Allowed redirect URIs" to enable OAuth banks (Chase, BofA, Wells, Citi, Cap1). Retrying without redirect_uri — OAuth banks will be hidden.`,
        { redirect_uri: redirectUri },
      );
      const resp = await linkTokenCreate(creds, {});
      linkToken = resp.link_token;
    } else {
      throw err;
    }
  }

  // Save the in-flight session in case Plaid Link does a full-page
  // OAuth redirect (mobile / popup-blocked). resumeOAuthLinkIfPending
  // picks it up on the next page load.
  if (redirectUri) {
    savePendingSession({
      link_token: linkToken,
      created_at: new Date().toISOString(),
      redirect_uri: redirectUri,
    });
  }

  try {
    const { publicToken, metadata } = await openLinkAndCaptureSuccess({ linkToken });
    return await finalizeNewBank(creds, publicToken, metadata, picker);
  } finally {
    clearPendingSession();
  }
}

/** Detect an OAuth-return URL (?oauth_state_id=...) on app boot. If
 *  present + we have a saved Link session, resume the flow by
 *  reconstructing Plaid Link with the original link_token + the full
 *  redirect URL (passed as `receivedRedirectUri`). Returns the new
 *  PlaidItem on success, null if no pending OAuth was detected or
 *  the session expired.
 *
 *  Cleans `?oauth_state_id` from the visible URL via
 *  history.replaceState so a refresh doesn't re-trigger resume after
 *  the flow completes. */
export async function resumeOAuthLinkIfPending(
  creds: PlaidCredentials,
  picker: AccountPicker,
): Promise<PlaidItem | null> {
  if (typeof window === "undefined") return null;
  const stateId = new URLSearchParams(window.location.search).get("oauth_state_id");
  if (!stateId) return null;

  const session = getPendingSession();
  if (!session) {
    log.warn(
      "plaid",
      "OAuth return URL detected but no saved Link session — the session may have expired or the page is a deep link. Ignoring.",
    );
    return null;
  }

  // Capture the full URL (with state id) BEFORE cleaning, so Plaid
  // Link can extract the state id from it. Then clean visible URL via
  // window.history (not bare `history`) so test mocks can intercept.
  const receivedRedirectUri = window.location.href;
  const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(null, "", cleanUrl);
  log.info("plaid", "Resuming Plaid Link from OAuth redirect", {
    redirect_uri: session.redirect_uri,
  });

  try {
    const { publicToken, metadata } = await openLinkAndCaptureSuccess({
      linkToken: session.link_token,
      receivedRedirectUri,
    });
    return await finalizeNewBank(creds, publicToken, metadata, picker);
  } finally {
    clearPendingSession();
  }
}

/** Shared body of connectNewBank + resumeOAuthLinkIfPending — open
 *  Plaid Link with whatever token / receivedRedirectUri the caller
 *  has, wait for onSuccess to capture (public_token, metadata), and
 *  throw if Plaid Link closed without delivering them. */
async function openLinkAndCaptureSuccess(args: {
  linkToken: string;
  receivedRedirectUri?: string;
}): Promise<{ publicToken: string; metadata: PlaidLinkSuccessMetadata }> {
  // onEvent + onLoad land in the activity log under `plaid-link`;
  // onSuccess / onExit log under `plaid` so a post-HANDOFF hang shows
  // exactly where Link stopped. See the C15 commit for the rationale —
  // Plaid's Layer flow sometimes fires onSuccess but never onExit.
  let publicToken: string | null = null;
  let onSuccessMeta: PlaidLinkSuccessMetadata | null = null;
  let exitErr: { error_message?: string | null; display_message?: string | null } | null = null;
  log.info("plaid", "openPlaidLink: calling Plaid.create + open", {
    isResume: !!args.receivedRedirectUri,
  });
  await openPlaidLink({
    linkToken: args.linkToken,
    receivedRedirectUri: args.receivedRedirectUri,
    onSuccess: (token, meta) => {
      log.info("plaid", "onSuccess fired", {
        hasToken: !!token,
        institution: meta?.institution?.name ?? null,
        accountCount: meta?.accounts?.length ?? 0,
      });
      publicToken = token;
      onSuccessMeta = meta;
    },
    onExit: (err, metadata) => {
      log.info("plaid", "onExit fired", {
        hasError: !!err,
        errorCode: err?.error_code ?? null,
        status: metadata?.status ?? null,
      });
      if (err) exitErr = err;
    },
    onLoad: () => {
      log.info("plaid-link", "load: ready");
    },
    onEvent: (eventName, metadata) => {
      log.info("plaid-link", `event: ${eventName}`, metadata);
    },
  });
  log.info("plaid", "openPlaidLink: resolved", {
    havePublicToken: !!publicToken,
    haveMetadata: !!onSuccessMeta,
    haveExitErr: !!exitErr,
  });
  if (!publicToken || !onSuccessMeta) {
    if (exitErr) {
      const e = exitErr as { error_message?: string | null; display_message?: string | null };
      throw new Error(
        e.display_message ?? e.error_message ?? "Plaid Link exited without linking a bank.",
      );
    }
    throw new Error("Plaid Link was closed without linking a bank.");
  }
  return { publicToken, metadata: onSuccessMeta };
}

/** Exchange the public_token, ask the user which accounts to import,
 *  and persist the resulting PlaidItem. Shared by connectNewBank and
 *  resumeOAuthLinkIfPending. */
async function finalizeNewBank(
  creds: PlaidCredentials,
  publicToken: string,
  metadata: PlaidLinkSuccessMetadata,
  picker: AccountPicker,
): Promise<PlaidItem> {
  log.info("plaid", "exchangePublicToken: start");
  const exchanged = await exchangePublicToken(creds, publicToken);
  log.info("plaid", "exchangePublicToken: done", {
    hasAccessToken: !!exchanged.access_token,
    itemId: exchanged.item_id,
  });

  const knownAccounts = metadataToKnownAccounts(metadata);
  const institutionName = metadata.institution?.name?.trim() || "(unknown bank)";
  log.info("plaid", "pickForNewItem: opening", {
    institutionName,
    accountCount: knownAccounts.length,
  });
  let selected: string[];
  try {
    const picked = await picker.pickForNewItem({ institutionName, accounts: knownAccounts });
    selected = picked.selected;
    log.info("plaid", "pickForNewItem: resolved", { selectedCount: selected.length });
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
  if (selected.length === 0) {
    throw new Error("Pick at least one account, or cancel to skip this bank.");
  }

  const item: PlaidItem = {
    item_id: exchanged.item_id,
    access_token: exchanged.access_token,
    institution_id: metadata.institution?.institution_id,
    institution_name: institutionName,
    last_cursor: "",
    last_sync: null,
    created_at: new Date().toISOString(),
    selected_account_ids: selected,
    known_accounts: knownAccounts,
  };
  await upsertItem(item);
  return item;
}

// ── pullAllItems ───────────────────────────────────────────────────

/** Sync every linked item once. Each item's sync runs sequentially —
 *  Plaid rate-limits per client_id and we don't want to risk that.
 *  An individual item failing is logged via writer.reportItemError
 *  but doesn't halt the loop. */
export async function pullAllItems(
  creds: PlaidCredentials,
  writer: SyncWriter,
): Promise<{ successCount: number; failedCount: number }> {
  const items = await listItems();
  let successCount = 0;
  let failedCount = 0;
  for (const item of items) {
    try {
      const result = await pullPlaidSync(creds, item);
      await writer.acceptResult(item, result);
      await updateItemAfterSync(item.item_id, result.finalCursor, new Date());
      // Refresh known_accounts so a newly-added bank account at this
      // institution shows up in the Manage Banks selector next time.
      if (result.observedAccounts.length > 0) {
        const refreshed: PlaidItem = {
          ...item,
          last_cursor: result.finalCursor,
          last_sync: new Date().toISOString(),
          known_accounts: result.observedAccounts.map((a) => ({
            account_id: a.account_id,
            name: a.name ?? "",
            mask: a.mask ?? null,
            subtype: a.subtype ?? null,
            type: a.type ?? null,
          })),
        };
        await upsertItem(refreshed);
      }
      successCount++;
    } catch (err) {
      failedCount++;
      // If Plaid said the bank invalidated our session, flag the item
      // so the Manage Banks UI can surface a "Re-link" button. Same
      // detection happens in the single-item pull path in main.ts.
      if (err instanceof PlaidApiError && err.body.error_code === "ITEM_LOGIN_REQUIRED") {
        await setItemNeedsRelink(item.item_id, true).catch(() => undefined);
      }
      writer.reportItemError?.(item, err);
    }
  }
  return { successCount, failedCount };
}

/** Inspect an error caught from pullPlaidSync. If it's
 *  ITEM_LOGIN_REQUIRED, flip the item's needs_relink flag so the UI
 *  shows the re-link button. Safe to call regardless of error type
 *  (no-op for non-PlaidApiError, non-LOGIN_REQUIRED). Returns true if
 *  a re-link flag was set. */
export async function maybeFlagRelink(itemId: string, err: unknown): Promise<boolean> {
  if (err instanceof PlaidApiError && err.body.error_code === "ITEM_LOGIN_REQUIRED") {
    await setItemNeedsRelink(itemId, true).catch(() => undefined);
    return true;
  }
  return false;
}

// ── updateItemSelection ────────────────────────────────────────────

/** Change which account_ids an existing item imports. Used by the
 *  Manage Banks "edit accounts" flow. */
export async function updateItemSelection(
  itemId: string,
  selectedAccountIds: string[],
): Promise<PlaidItem> {
  const items = await listItems();
  const item = items.find((it) => it.item_id === itemId);
  if (!item) throw new Error(`No Plaid item with id ${itemId}`);
  const updated: PlaidItem = { ...item, selected_account_ids: selectedAccountIds };
  await upsertItem(updated);
  return updated;
}

// ── reset & re-pull ────────────────────────────────────────────────

/** Clear an item's cursor so the next /transactions/sync re-fetches
 *  every transaction Plaid still has for it (default 24-month window).
 *  Keeps last_sync so pullPlaidSync's "fresh item" warm-up retries
 *  don't fire — Plaid already has the data; an empty response here
 *  would be legitimate, not a warm-up signal.
 *
 *  Dashboard transactions stay deduped by hash id when the re-pull
 *  hits processFiles, so already-imported rows aren't duplicated. */
export async function resetItemCursor(itemId: string): Promise<PlaidItem> {
  const items = await listItems();
  const item = items.find((it) => it.item_id === itemId);
  if (!item) throw new Error(`No Plaid item with id ${itemId}`);
  const updated: PlaidItem = { ...item, last_cursor: "" };
  await upsertItem(updated);
  return updated;
}

/** Clear every item's cursor in one go. Used by the global "Reset all
 *  & re-pull" entry point in Manage Banks. Returns the number of
 *  items reset (so the UI can say "Reset N banks; pulling…"). */
export async function resetAllCursors(): Promise<number> {
  const items = await listItems();
  for (const item of items) {
    if (item.last_cursor === "") continue; // already clear
    await upsertItem({ ...item, last_cursor: "" });
  }
  return items.length;
}

// ── re-link (ITEM_LOGIN_REQUIRED recovery) ─────────────────────────

/** Re-authenticate an existing Item whose access_token Plaid invalidated
 *  (typically after the user changed their bank password). Runs Plaid
 *  Link in "update mode" — same access_token, same item_id, just a
 *  fresh login challenge.
 *
 *  Flow:
 *    1. POST /link/token/create with access_token set, products omitted.
 *    2. Open Plaid Link with the resulting link_token.
 *    3. User re-enters bank credentials.
 *    4. onSuccess fires (public_token may be null/empty in update
 *       mode — we don't need it; the item's existing access_token is
 *       now valid again).
 *    5. Clear the item's needs_relink flag.
 *
 *  Returns the refreshed PlaidItem. Throws if Plaid Link is closed
 *  without completing — caller's catch surfaces the error. The
 *  user can re-trigger the flow from the same button. */
export async function reLinkItem(creds: PlaidCredentials, item: PlaidItem): Promise<PlaidItem> {
  const linkTokenResp = await linkTokenCreate(creds, { accessToken: item.access_token });

  let completed = false;
  let exitErr: { error_message?: string | null; display_message?: string | null } | null = null;
  log.info("plaid", "reLinkItem: calling Plaid.create + open (update mode)", {
    item: item.institution_name,
  });
  await openPlaidLink({
    linkToken: linkTokenResp.link_token,
    onSuccess: () => {
      log.info("plaid", "reLinkItem: onSuccess fired (re-authenticated)", {
        item: item.institution_name,
      });
      completed = true;
    },
    onExit: (err) => {
      log.info("plaid", "reLinkItem: onExit fired", {
        item: item.institution_name,
        hasError: !!err,
        errorCode: err?.error_code ?? null,
      });
      if (err) exitErr = err;
    },
    onLoad: () => {
      log.info("plaid-link", "load: ready (re-link)");
    },
    onEvent: (eventName, metadata) => {
      log.info("plaid-link", `re-link event: ${eventName}`, metadata);
    },
  });

  if (!completed) {
    if (exitErr) {
      const e = exitErr as { error_message?: string | null; display_message?: string | null };
      throw new Error(
        e.display_message ?? e.error_message ?? "Re-link did not complete.",
      );
    }
    throw new Error("Re-link was closed before completion.");
  }

  // Clear the needs_relink flag so the UI removes the warning badge.
  // upsertItem preserves the existing access_token, item_id, accounts,
  // cursor, etc. — only the flag changes.
  const refreshed: PlaidItem = { ...item, needs_relink: undefined };
  await upsertItem(refreshed);
  return refreshed;
}
